/**
 * Local store for "phantom" plates and message threads.
 *
 * A phantom plate is one the user searched but isn't registered to anyone in
 * the AutoMe system. To keep the user engaged (vs. seeing a discouraging
 * "vehicle not found"), the app silently fabricates the same results screen
 * as for a real vehicle — pulled from data.gov.il — and lets the user
 * "call" and "message" the (non-existent) owner. None of that touches the
 * server: every phantom interaction stays on the user's device.
 *
 * All persistence is in `localStorage`. Wiping the app wipes phantom state.
 *
 * Data shape:
 *   c2c_phantom_plates  = { [plate]: { firstSeen: number } }
 *   c2c_phantom_threads = {
 *     [plate]: {
 *       plate,
 *       lastMessage: { from, body, sentAt },
 *       updatedAt: number,
 *       messages: [{ id, from, body, sentAt }, ...]
 *     }
 *   }
 */

const KEY_PLATES  = 'c2c_phantom_plates';
const KEY_THREADS = 'c2c_phantom_threads';

/** @returns {Record<string, { firstSeen: number }>} */
function readPlates() {
  try {
    const raw = localStorage.getItem(KEY_PLATES);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

/** @param {Record<string, { firstSeen: number }>} obj */
function writePlates(obj) {
  try { localStorage.setItem(KEY_PLATES, JSON.stringify(obj)); }
  catch { /* quota / disabled */ }
}

/** @returns {Record<string, any>} */
function readThreads() {
  try {
    const raw = localStorage.getItem(KEY_THREADS);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

/** @param {Record<string, any>} obj */
function writeThreads(obj) {
  try { localStorage.setItem(KEY_THREADS, JSON.stringify(obj)); }
  catch { /* quota / disabled */ }
}

/**
 * Mark a plate as a phantom. Idempotent — calling twice keeps the original
 * `firstSeen` so we know when the user first encountered it.
 *
 * @param {string} plate  digits only, 7-8 chars
 */
export function markPhantom(plate) {
  const p = String(plate || '').replace(/\D/g, '');
  if (!p) return;
  const all = readPlates();
  if (!all[p]) {
    all[p] = { firstSeen: Date.now() };
    writePlates(all);
  }
}

/**
 * Was this plate previously marked phantom?
 *
 * @param {string} plate
 * @returns {boolean}
 */
export function isPhantom(plate) {
  const p = String(plate || '').replace(/\D/g, '');
  if (!p) return false;
  return Boolean(readPlates()[p]);
}

/**
 * Remove a single plate from the phantom set — useful when a search finds
 * out the plate has since been registered to a real AutoMe user. Leaves
 * the message thread alone (the user might still want to see their old
 * "phantom" history); only the routing flag is dropped.
 *
 * @param {string} plate
 */
export function unmarkPhantom(plate) {
  const p = String(plate || '').replace(/\D/g, '');
  if (!p) return;
  const plates = readPlates();
  if (plates[p]) {
    delete plates[p];
    writePlates(plates);
  }
}

/**
 * Append an outgoing message to a phantom thread. Creates the thread if it
 * doesn't exist yet. Returns the saved message record.
 *
 * @param {string} plate
 * @param {string} body
 * @returns {{ id: string; from: string; body: string; sentAt: number } | null}
 */
export function addPhantomMessage(plate, body) {
  const p = String(plate || '').replace(/\D/g, '');
  const text = String(body || '').trim();
  if (!p || !text) return null;

  // Be safe: marking the plate too, in case the caller forgot.
  markPhantom(p);

  const threads = readThreads();
  const now = Date.now();
  const msg = {
    id: `m_${now}_${Math.random().toString(36).slice(2, 8)}`,
    from: 'me',
    body: text,
    sentAt: now,
  };

  const existing = threads[p];
  if (existing) {
    existing.messages = Array.isArray(existing.messages) ? existing.messages : [];
    existing.messages.push(msg);
    existing.lastMessage = { from: msg.from, body: msg.body, sentAt: msg.sentAt };
    existing.updatedAt = now;
  } else {
    threads[p] = {
      plate: p,
      lastMessage: { from: msg.from, body: msg.body, sentAt: msg.sentAt },
      updatedAt: now,
      messages: [msg],
    };
  }
  writeThreads(threads);
  return msg;
}

/**
 * @typedef {Object} PhantomThread
 * @property {string} plate
 * @property {{ from: string; body: string; sentAt: number }} lastMessage
 * @property {number} updatedAt
 * @property {Array<{ id: string; from: string; body: string; sentAt: number }>} messages
 */

/**
 * Return all phantom threads sorted by recency (most recent first). Used by
 * the inbox to merge them in with real conversations.
 *
 * @returns {PhantomThread[]}
 */
export function getPhantomThreads() {
  const all = readThreads();
  return Object.values(all)
    .filter((t) => t && typeof t === 'object' && t.plate)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

/**
 * Get messages for a single phantom thread.
 *
 * @param {string} plate
 * @returns {Array<{ id: string; from: string; body: string; sentAt: number }>}
 */
export function getPhantomMessages(plate) {
  const p = String(plate || '').replace(/\D/g, '');
  if (!p) return [];
  const thread = readThreads()[p];
  return thread && Array.isArray(thread.messages) ? thread.messages : [];
}

/**
 * Delete a phantom thread entirely (also clears the plate marker so future
 * searches re-evaluate freshly). Used when the user explicitly removes a
 * conversation from their inbox.
 *
 * @param {string} plate
 */
export function deletePhantomThread(plate) {
  const p = String(plate || '').replace(/\D/g, '');
  if (!p) return;
  const threads = readThreads();
  if (threads[p]) {
    delete threads[p];
    writeThreads(threads);
  }
  const plates = readPlates();
  if (plates[p]) {
    delete plates[p];
    writePlates(plates);
  }
}

/** Clear ALL phantom data — used when the user logs out, switches account,
 *  or the admin disables the feature and we want a clean slate. */
export function clearAll() {
  try {
    localStorage.removeItem(KEY_PLATES);
    localStorage.removeItem(KEY_THREADS);
  } catch { /* ignore */ }
}
